import Lang from '/@/modules/core/app/services/Lang';
import { Color } from '@/modules/core/app/enums/Color';
import Enum from '@/modules/core/app/helpers/Enum';
import RealEstate from '/@/assets/illustrations/real-estate.svg';
import SavingBank from '/@/assets/illustrations/saving-bank.svg';
import User from '/@/assets/illustrations/user.svg';
import DiamondRing from '/@/assets/illustrations/diamond-ring.svg';
import type { Component } from 'vue';
import { LoadComponentService } from '@/modules/core/app/services/LoadComponentService';
import { FinalTaxesAssetTypeKey } from '@/modules/tax/constants/FinalTaxesAssetTypeKey';
import { camelCase } from 'lodash';

const FinalTaxesRealEstateInputTable: Promise<Component> = import(
  '@/modules/tax/components/stateful/finaltaxescalculator/FinalTaxesRealEstateInputTable.vue'
);
const FinalTaxesBankAccountInputTable: Promise<Component> = import(
  '@/modules/tax/components/stateful/finaltaxescalculator/FinalTaxesBankAccountInputTable.vue'
);
const FinalTaxesOtherInvestmentInputTable: Promise<Component> = import(
  '@/modules/tax/components/stateful/finaltaxescalculator/FinalTaxesOtherInvestmentInputTable.vue'
);
const FinalTaxesBusinessInterestInputTable: Promise<Component> = import(
  '@/modules/tax/components/stateful/finaltaxescalculator/FinalTaxesBusinessInterestInputTable.vue'
);
const FinalTaxesArtValuableInputTable: Promise<Component> = import(
  '@/modules/tax/components/stateful/finaltaxescalculator/FinalTaxesArtValuableInputTable.vue'
);

export class FinalTaxesAssetType extends Enum {
  key!: FinalTaxesAssetTypeKey;
  dataKey!: string;
  value!: string;
  description!: string;
  color!: string;
  image!: string;
  component?: Component;
  getComponent?: () => Promise<void>;

  static REAL_ESTATE: FinalTaxesAssetType = {
    key: FinalTaxesAssetTypeKey.REAL_ESTATE,
    get dataKey(): string {
      return camelCase(FinalTaxesAssetTypeKey.REAL_ESTATE);
    },
    get value(): string {
      return Lang.t('tax.asset-real-estate');
    },
    get description(): string {
      return Lang.t('tax.asset-real-estate-desc');
    },
    color: Color.PEWTER_BLUE,
    image: RealEstate,
    async getComponent(): Promise<void> {
      this.component = await LoadComponentService.load(FinalTaxesRealEstateInputTable);
    }
  };

  static BANK_ACCOUNT: FinalTaxesAssetType = {
    key: FinalTaxesAssetTypeKey.BANK_ACCOUNT,
    get dataKey(): string {
      return camelCase(FinalTaxesAssetTypeKey.BANK_ACCOUNT);
    },
    get value(): string {
      return Lang.t('tax.asset-bank-account');
    },
    get description(): string {
      return Lang.t('tax.asset-bank-account-desc');
    },
    color: Color.ROYAL_BLUE,
    image: SavingBank,
    async getComponent(): Promise<void> {
      this.component = await LoadComponentService.load(FinalTaxesBankAccountInputTable);
    }
  };

  static OTHER_INVESTMENT: FinalTaxesAssetType = {
    key: FinalTaxesAssetTypeKey.OTHER_INVESTMENT,
    get dataKey(): string {
      return camelCase(FinalTaxesAssetTypeKey.OTHER_INVESTMENT);
    },
    get value(): string {
      return Lang.t('tax.asset-other-investments');
    },
    get description(): string {
      return Lang.t('tax.asset-other-investments-desc');
    },
    color: Color.CHERRY_RED,
    image: SavingBank,
    async getComponent(): Promise<void> {
      this.component = await LoadComponentService.load(FinalTaxesOtherInvestmentInputTable);
    }
  };

  static BUSINESS_INTEREST: FinalTaxesAssetType = {
    key: FinalTaxesAssetTypeKey.BUSINESS_INTEREST,
    get dataKey(): string {
      return camelCase(FinalTaxesAssetTypeKey.BUSINESS_INTEREST);
    },
    get value(): string {
      return Lang.t('tax.asset-business-interests');
    },
    get description(): string {
      return Lang.t('tax.asset-business-interests-desc');
    },
    color: Color.PUMPKIN_ORANGE,
    image: User,
    async getComponent(): Promise<void> {
      this.component = await LoadComponentService.load(FinalTaxesBusinessInterestInputTable);
    }
  };

  static ART_JEWELRY: FinalTaxesAssetType = {
    key: FinalTaxesAssetTypeKey.ART_JEWELRY,
    get dataKey(): string {
      return camelCase(FinalTaxesAssetTypeKey.ART_JEWELRY);
    },
    get value(): string {
      return Lang.t('tax.asset-valuables');
    },
    get description(): string {
      return Lang.t('tax.asset-valuables-desc');
    },
    color: Color.LILAC_PINK,
    image: DiamondRing,
    async getComponent(): Promise<void> {
      this.component = await LoadComponentService.load(FinalTaxesArtValuableInputTable);
    }
  };
}
